import React, { useEffect, useState } from "react";
import {
  Play,
  CheckCircle2,
  CalendarRange,
  RefreshCw,
  AlertTriangle,
  MessageSquare,
  History,
  Clock,
  PauseCircle,
  ArrowRightCircle,
  ListChecks,
  ListPlus
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getTaskActivityApi } from "../../app/api";
 
const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
/* ================= HELPERS ================= */
 
const safeParse = (val) => {
  try {
    if (!val) return {};
    if (typeof val === "object") return val;
    return JSON.parse(val);
  } catch {
    return val || {};
  }
};
 
 
const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(String(date).replace(' ', 'T'));
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};
 
const STATUS_LABEL = {
  TODO: "To Do",
  IN_PROGRESS: "In Progress",
  REVIEW: "In Review",
  HOLD: "On Hold",
  DONE: "Completed",
};
 
const getStatusColor = (status) => {
  switch (status) {
    case "TODO": return "text-gray-500";
    case "IN_PROGRESS": return "text-blue-500";
    case "REVIEW": return "text-purple-500";
    case "HOLD": return "text-orange-500";
    case "DONE": return "text-emerald-500";
    default: return "text-gray-400";
  }
};
 
const getActivityConfig = (a) => {
  const oldData = safeParse(a.old_value);
  const newData = safeParse(a.new_value);
 
  const reason = newData.reason || oldData.reason;
  const newDueDate = newData.new_due_date || oldData.new_due_date;
  const oldDueDate = oldData.old_due_date || oldData.due_date;
 
  switch (a.action) {
    case "TASK_STARTED":
      return {
        label: "Task Started",
        icon: Play,
        color: "bg-green-500",
        shadow: "shadow-green-200",
        details: null
      };
    case "TASK_COMPLETED":
      return {
        label: "Task Completed",
        icon: CheckCircle2,
        color: "bg-emerald-600",
        shadow: "shadow-emerald-200",
        details: reason ? (
          <div className="bg-emerald-50 border-l-2 border-emerald-400 p-2 text-[11px] italic text-emerald-800 rounded">
            <span className="font-semibold not-italic">Note: </span>"{reason}"
          </div>
        ) : "Goal reached! Task is now marked as DONE."
      };
    case "TASK_COMPLETED_LATE":
      return {
        label: "Completed (Late)",
        icon: CheckCircle2,
        color: "bg-rose-600",
        shadow: "shadow-rose-300",
        details: (
          <div className="space-y-1.5 text-rose-800">
            {reason ? (
              <div className="bg-rose-50 border-l-2 border-rose-400 p-2 text-[11px] italic rounded shadow-sm">
                <span className="font-bold not-italic text-rose-600">Reason: </span>
                <span className="opacity-90">"{reason}"</span>
              </div>
            ) : (
              <div className="text-[11px] font-medium opacity-80 italic">✨ Delayed but delivered!</div>
            )}
          </div>
        )
      };
    case "TASK_OVERDUE":
      return {
        label: "Task Overdue",
        icon: AlertTriangle,
        color: "bg-red-600",
        shadow: "shadow-red-300",
        details: `This task missed its deadline`
      };
 
    case "STATUS_UPDATED": {
      const fromStatus = oldData.status;
      const toStatus = newData.status;
      const isHold = toStatus === "HOLD";
 
      const iconMap = {
        HOLD: PauseCircle,
        IN_PROGRESS: ArrowRightCircle,
        REVIEW: ListChecks,
        DONE: CheckCircle2,
        TODO: RefreshCw,
      };
 
      const colorMap = {
        HOLD: "bg-orange-500",
        IN_PROGRESS: "bg-blue-500",
        REVIEW: "bg-purple-500",
        DONE: "bg-emerald-500",
        TODO: "bg-gray-400",
      };
 
      const shadowMap = {
        HOLD: "shadow-orange-200",
        IN_PROGRESS: "shadow-blue-200",
        REVIEW: "shadow-purple-200",
        DONE: "shadow-emerald-200",
        TODO: "shadow-gray-100",
      };
 
      return {
        label: isHold ? "Task Put On Hold" : `Moved to ${STATUS_LABEL[toStatus] || toStatus}`,
        icon: iconMap[toStatus] || RefreshCw,
        color: colorMap[toStatus] || "bg-gray-400",
        shadow: shadowMap[toStatus] || "shadow-gray-100",
        details: (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-medium">
              <span className={`font-bold ${getStatusColor(fromStatus)}`}>
                {STATUS_LABEL[fromStatus] || fromStatus}
              </span>
              <ArrowRightCircle className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className={`font-bold ${getStatusColor(toStatus)}`}>
                {STATUS_LABEL[toStatus] || toStatus}
              </span>
            </div>
            {isHold && reason && (
              <div className="bg-orange-50 border-l-2 border-orange-400 p-2 text-[11px] italic text-orange-800 rounded">
                <span className="font-semibold not-italic">Reason: </span>"{reason}"
              </div>
            )}
          </div>
        )
      };
    }
 
    case "DUE_DATE_UPDATED":
      return {
        label: "Timeline Adjusted",
        icon: CalendarRange,
        color: "bg-amber-500",
        shadow: "shadow-amber-200",
        details: (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 line-through">
                {oldDueDate ? String(oldDueDate).split(/[\sT]/)[0] : 'No Date'}
              </span>
              <span className="text-amber-600 font-semibold">
                → {newDueDate ? String(newDueDate).split(/[\sT]/)[0] : '—'}
              </span>
            </div>
            {reason && (
              <div className="bg-amber-50 border-l-2 border-amber-400 p-2 text-[11px] italic text-amber-800 rounded">
                "{reason}"
              </div>
            )}
          </div>
        )
      };
    case "UPDATE": {
      const updatedFields = [];
      if (newData.due_date) {
        const d = new Date(newData.due_date);
        const dateString = isNaN(d) ? newData.due_date : d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
        updatedFields.push(`Due date → ${dateString}`);
      }
      if (newData.status) {
        updatedFields.push(`Status → ${STATUS_LABEL[newData.status] || newData.status}`);
      }
      if (newData.priority) {
        updatedFields.push(`Priority → ${newData.priority}`);
      }
      return {
        label: "Task Updated",
        icon: RefreshCw,
        color: "bg-gray-400",
        shadow: "shadow-gray-100",
        details: updatedFields.length > 0 ? (
          <div className="space-y-1">
            {updatedFields.map((f, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
                <div className="w-1 h-1 rounded-full bg-gray-400" />
                {f}
              </div>
            ))}
          </div>
        ) : null
      };
    }
 
    case "COMMENT":
      return {
        label: "Shared a thought",
        icon: MessageSquare,
        color: "bg-indigo-500",
        shadow: "shadow-indigo-200",
        details: "New comment added to the discussion."
      };
    default:
      return {
        label: a.action,
        icon: History,
        color: "bg-gray-400",
        shadow: "shadow-gray-100",
        details: null
      };
  }
};
 
/* ================= COMPONENT ================= */
 
export default function TaskActivity2({ taskId, activities, refreshKey = 0 }) {
  const [activity, setActivity] = useState(activities || []);
  const [loading, setLoading] = useState(!activities);
  const [showAll, setShowAll] = useState(false);
 
  useEffect(() => {
    if (activities) {
      setActivity(activities.filter(a => a.action !== 'SUBTASK_ADDED'));
      setLoading(false);
      return;
    }
 
    if (!taskId) return;
    setLoading(true);
 
    getTaskActivityApi(taskId)
      .then((res) => {
        const rows = Array.isArray(res) ? res : res?.data || [];
        setActivity(rows.filter(a => a.action !== 'SUBTASK_ADDED'));
      })
      .catch(() => setActivity([]))
      .finally(() => setLoading(false));
  }, [taskId, activities, refreshKey]);
 
  if (loading) {
    return (
      <section className="flex flex-col items-center justify-center space-y-3 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        <span className="translate-x-1 text-xs font-bold tracking-wide text-slate-400">Tracking journey...</span>
      </section>
    );
  }
 
  if (!activity.length) {
    return (
      <section className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-10 shadow-sm">
        <History className="mb-2 h-8 w-8 text-slate-300" />
        <span className="text-xs font-bold text-slate-400">No updates recorded yet</span>
      </section>
    );
  }
 
  const displayedActivity = showAll ? activity : activity.slice(0, 5);
 
  return (
    <section className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-[0.14em] text-slate-700">Activity</h3>
          <p className="mt-1 text-xs font-medium text-slate-400">Latest movements on this card</p>
        </div>
        <History size={18} className="text-blue-500" />
      </div>

      <div className="relative space-y-0.5">
        <div className="absolute bottom-2 left-4 top-2 w-0.5 bg-gradient-to-b from-blue-300 via-slate-200 to-transparent" />
 
        <AnimatePresence mode="popLayout">
          {displayedActivity.map((a, i) => {
            const config = getActivityConfig(a);
            // const dateStr = formatSimpleDate(a.created_at);
            const dateStr = formatDateTime(a.created_at);
            const Icon = config.icon;
 
            return (
              <motion.div
                key={a.id || i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="group relative flex gap-5 pb-6 pl-2 last:pb-1"
              >
                <div className="relative z-10 pt-1">
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full ${config.color} ${config.shadow} shadow-lg ring-4 ring-white transition-transform group-hover:scale-110`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
 
                <div className="flex-1 -mt-0.5">
                  <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                    <span className="text-[15px] font-black text-slate-800 transition-colors group-hover:text-blue-600">
                      {config.label}
                    </span>
                    <div className="flex w-fit items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-600 shadow-sm">
                      <Clock className="w-3 h-3" />
                      {dateStr}
                    </div>
                  </div>
 
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded-full border border-white bg-slate-100 shadow-sm">
                      {a.user_profile_pic ? (
                        <img
                          src={`${IMAGE_BASE_URL}${a.user_profile_pic}`}
                          alt={a.user_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-blue-100 text-[10px] font-black italic text-blue-700">
                          {a.user_name?.charAt(0) || "?"}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] font-bold text-slate-600">{a.user_name || "System"}</span>
                  </div>
 
                  {config.details && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px] font-semibold leading-relaxed text-slate-700 shadow-sm transition-all group-hover:bg-white">
                      {config.details}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
 
      {activity.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-blue-100 bg-blue-50/60 py-2 text-[12px] font-black text-blue-700 transition hover:bg-blue-100"
        >
          {showAll ? "Show Less" : `View ${activity.length - 5} More Events`}
        </button>
      )}
 
      {!showAll && activity.length > 5 && (
        <div className="pointer-events-none absolute bottom-10 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent" />
      )}
    </section>
  );
}
 


// import React, { useEffect, useState } from "react";
// import {
//   Play,
//   CheckCircle2,
//   CalendarRange,
//   RefreshCw,
//   AlertTriangle,
//   MessageSquare,
//   History,
//   Clock,
//   PauseCircle,
//   ArrowRightCircle,
//   ListChecks,
//   ListPlus
// } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { getTaskActivityApi } from "../../app/api";
 
// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
// /* ================= HELPERS ================= */
 
// const safeParse = (val) => {
//   try {
//     if (!val) return {};
//     if (typeof val === "object") return val;
//     return JSON.parse(val);
//   } catch {
//     return val || {};
//   }
// };
 
// // const formatSimpleDate = (date) => {
// //   if (!date) return "";
// //   const d = new Date(String(date).replace(' ', 'T'));
// //   return d.toLocaleDateString("en-IN", {
// //     day: "numeric",
// //     month: "short",
// //     timeZone: "Asia/Kolkata"
// //   });
// // };


// const formatDateTime = (date) => {
//   if (!date) return "";
//   const d = new Date(String(date).replace(' ', 'T'));
//   return d.toLocaleString("en-IN", {
//     day: "numeric",
//     month: "short",
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true
//   });
// };
 
// const STATUS_LABEL = {
//   TODO: "To Do",
//   IN_PROGRESS: "In Progress",
//   REVIEW: "In Review",
//   HOLD: "On Hold",
//   DONE: "Completed",
// };
 
// const getStatusColor = (status) => {
//   switch (status) {
//     case "TODO": return "text-gray-500";
//     case "IN_PROGRESS": return "text-blue-500";
//     case "REVIEW": return "text-purple-500";
//     case "HOLD": return "text-orange-500";
//     case "DONE": return "text-emerald-500";
//     default: return "text-gray-400";
//   }
// };
 
// const getActivityConfig = (a) => {
//   const oldData = safeParse(a.old_value);
//   const newData = safeParse(a.new_value);
 
//   const reason = newData.reason || oldData.reason;
//   const newDueDate = newData.new_due_date || oldData.new_due_date;
//   const oldDueDate = oldData.old_due_date || oldData.due_date;
 
//   switch (a.action) {
//     case "TASK_STARTED":
//       return {
//         label: "Task Started",
//         icon: Play,
//         color: "bg-green-500",
//         shadow: "shadow-green-200",
//         details: null
//       };
//     case "TASK_COMPLETED":
//       return {
//         label: "Task Completed",
//         icon: CheckCircle2,
//         color: "bg-emerald-600",
//         shadow: "shadow-emerald-200",
//         details: "Goal reached! Task is now marked as DONE."
//       };
//     case "TASK_COMPLETED_LATE":
//       return {
//         label: "Completed (Late)",
//         icon: CheckCircle2,
//         color: "bg-amber-700",
//         shadow: "shadow-amber-300",
//         details: "Task finished past its deadline. Better late than never!"
//       };
//     case "TASK_OVERDUE":
//       return {
//         label: "Task Overdue",
//         icon: AlertTriangle,
//         color: "bg-red-600",
//         shadow: "shadow-red-300",
//         details: `This task missed its deadline (${oldDueDate ? String(oldDueDate).split(/[\sT]/)[0] : '—'}). Urgency required!`
//       };
 
//     case "STATUS_UPDATED": {
//       const fromStatus = oldData.status;
//       const toStatus = newData.status;
//       const isHold = toStatus === "HOLD";
 
//       const iconMap = {
//         HOLD: PauseCircle,
//         IN_PROGRESS: ArrowRightCircle,
//         REVIEW: ListChecks,
//         DONE: CheckCircle2,
//         TODO: RefreshCw,
//       };
 
//       const colorMap = {
//         HOLD: "bg-orange-500",
//         IN_PROGRESS: "bg-blue-500",
//         REVIEW: "bg-purple-500",
//         DONE: "bg-emerald-500",
//         TODO: "bg-gray-400",
//       };
 
//       const shadowMap = {
//         HOLD: "shadow-orange-200",
//         IN_PROGRESS: "shadow-blue-200",
//         REVIEW: "shadow-purple-200",
//         DONE: "shadow-emerald-200",
//         TODO: "shadow-gray-100",
//       };
 
//       return {
//         label: isHold ? "Task Put On Hold" : `Moved to ${STATUS_LABEL[toStatus] || toStatus}`,
//         icon: iconMap[toStatus] || RefreshCw,
//         color: colorMap[toStatus] || "bg-gray-400",
//         shadow: shadowMap[toStatus] || "shadow-gray-100",
//         details: (
//           <div className="space-y-1.5">
//             <div className="flex items-center gap-2 text-xs font-medium">
//               <span className={`font-bold ${getStatusColor(fromStatus)}`}>
//                 {STATUS_LABEL[fromStatus] || fromStatus}
//               </span>
//               <ArrowRightCircle className="w-3 h-3 text-gray-400 flex-shrink-0" />
//               <span className={`font-bold ${getStatusColor(toStatus)}`}>
//                 {STATUS_LABEL[toStatus] || toStatus}
//               </span>
//             </div>
//             {isHold && reason && (
//               <div className="bg-orange-50 border-l-2 border-orange-400 p-2 text-[11px] italic text-orange-800 rounded">
//                 <span className="font-semibold not-italic">Reason: </span>"{reason}"
//               </div>
//             )}
//           </div>
//         )
//       };
//     }
 
//     case "DUE_DATE_UPDATED":
//       return {
//         label: "Timeline Adjusted",
//         icon: CalendarRange,
//         color: "bg-amber-500",
//         shadow: "shadow-amber-200",
//         details: (
//           <div className="space-y-1">
//             <div className="flex items-center gap-2 text-xs">
//               <span className="text-gray-400 line-through">
//                 {oldDueDate ? String(oldDueDate).split(/[\sT]/)[0] : 'No Date'}
//               </span>
//               <span className="text-amber-600 font-semibold">
//                 → {newDueDate ? String(newDueDate).split(/[\sT]/)[0] : '—'}
//               </span>
//             </div>
//             {reason && (
//               <div className="bg-amber-50 border-l-2 border-amber-400 p-2 text-[11px] italic text-amber-800 rounded">
//                 "{reason}"
//               </div>
//             )}
//           </div>
//         )
//       };
//     case "UPDATE": {
//       const updatedFields = [];
//       if (newData.due_date) {
//         const d = new Date(newData.due_date);
//         const dateString = isNaN(d) ? newData.due_date : d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
//         updatedFields.push(`Due date → ${dateString}`);
//       }
//       if (newData.status) {
//         updatedFields.push(`Status → ${STATUS_LABEL[newData.status] || newData.status}`);
//       }
//       if (newData.priority) {
//         updatedFields.push(`Priority → ${newData.priority}`);
//       }
//       return {
//         label: "Task Updated",
//         icon: RefreshCw,
//         color: "bg-gray-400",
//         shadow: "shadow-gray-100",
//         details: updatedFields.length > 0 ? (
//           <div className="space-y-1">
//             {updatedFields.map((f, idx) => (
//               <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
//                 <div className="w-1 h-1 rounded-full bg-gray-400" />
//                 {f}
//               </div>
//             ))}
//           </div>
//         ) : null
//       };
//     }
//     // case "SUBTASK_ADDED": {
//     //   const subtaskTitle = newData.subtask_title || "—";
//     //   const subtaskDue = newData.subtask_due
//     //     ? new Date(newData.subtask_due).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })
//     //     : null;
//     //   return {
//     //     label: "Subtask Added",
//     //     icon: ListPlus,
//     //     color: "bg-violet-500",
//     //     shadow: "shadow-violet-200",
//     //     details: (
//     //       <div className="space-y-1">
//     //         <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium">
//     //           <ListPlus className="w-3 h-3 text-violet-500 flex-shrink-0" />
//     //           <span>{subtaskTitle}</span>
//     //         </div>
//     //         {subtaskDue && (
//     //           <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
//     //             <CalendarRange className="w-3 h-3" />
//     //             Due: {subtaskDue}
//     //           </div>
//     //         )}
//     //       </div>
//     //     )
//     //   };
//     // }
//     case "COMMENT":
//       return {
//         label: "Shared a thought",
//         icon: MessageSquare,
//         color: "bg-indigo-500",
//         shadow: "shadow-indigo-200",
//         details: "New comment added to the discussion."
//       };
//     default:
//       return {
//         label: a.action,
//         icon: History,
//         color: "bg-gray-400",
//         shadow: "shadow-gray-100",
//         details: null
//       };
//   }
// };
 
// /* ================= COMPONENT ================= */
 
// export default function TaskActivity2({ taskId, activities, refreshKey = 0 }) {
//   const [activity, setActivity] = useState(activities || []);
//   const [loading, setLoading] = useState(!activities);
//   const [showAll, setShowAll] = useState(false);
 
//   useEffect(() => {
//     if (activities) {
//       setActivity(activities.filter(a => a.action !== 'SUBTASK_ADDED'));
//       setLoading(false);
//       return;
//     }
 
//     if (!taskId) return;
//     setLoading(true);
 
//     getTaskActivityApi(taskId)
//       .then((res) => {
//         const rows = Array.isArray(res) ? res : res?.data || [];
//         setActivity(rows.filter(a => a.action !== 'SUBTASK_ADDED'));
//       })
//       .catch(() => setActivity([]))
//       .finally(() => setLoading(false));
//   }, [taskId, activities, refreshKey]);
 
//   if (loading) {
//     return (
//       <div className="flex flex-col items-center justify-center p-8 space-y-3">
//         <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
//         <span className="text-xs text-gray-400 font-medium tracking-wide translate-x-1">Tracking journey...</span>
//       </div>
//     );
//   }
 
//   if (!activity.length) {
//     return (
//       <div className="flex flex-col items-center justify-center p-10 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-100">
//         <History className="w-8 h-8 text-gray-200 mb-2" />
//         <span className="text-xs text-gray-400 font-medium">No updates recorded yet</span>
//       </div>
//     );
//   }
 
//   const displayedActivity = showAll ? activity : activity.slice(0, 5);
 
//   return (
//     <div className="relative">
//       <div className="relative space-y-0.5">
//         <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 via-gray-100 to-transparent" />
 
//         <AnimatePresence mode="popLayout">
//           {displayedActivity.map((a, i) => {
//             const config = getActivityConfig(a);
//             // const dateStr = formatSimpleDate(a.created_at);
//             const dateStr = formatDateTime(a.created_at);
//             const Icon = config.icon;
 
//             return (
//               <motion.div
//                 key={a.id || i}
//                 initial={{ opacity: 0, x: -10 }}
//                 animate={{ opacity: 1, x: 0 }}
//                 transition={{ delay: i * 0.05 }}
//                 className="group relative flex gap-6 pb-8 last:pb-2 pl-2"
//               >
//                 <div className="relative z-10 pt-1">
//                   <div className={`w-5 h-5 rounded-full ${config.color} ${config.shadow} shadow-lg flex items-center justify-center ring-4 ring-white group-hover:scale-110 transition-transform`}>
//                     <Icon className="w-2.5 h-2.5 text-white" />
//                   </div>
//                 </div>
 
//                 <div className="flex-1 -mt-0.5">
//                   <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
//                     <span className="text-[15px] font-bold text-black group-hover:text-blue-600 group-hover:bg-white p-1 rounded-lg transition-colors">
//                       {config.label}
//                     </span>
//                     <div className="flex items-center gap-2 text-[10px] shadow-lg text-black font-bold bg-gray-100 px-2 py-0.5 rounded-full w-fit">
//                       <Clock className="w-3 h-3" />
//                       {dateStr}
//                     </div>
//                   </div>
 
//                   <div className="flex items-center gap-2 mb-2">
//                     <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-100 border border-white shadow-sm flex-shrink-0">
//                       {a.user_profile_pic ? (
//                         <img
//                           src={`${IMAGE_BASE_URL}${a.user_profile_pic}`}
//                           alt={a.user_name}
//                           className="w-full h-full object-cover"
//                         />
//                       ) : (
//                         <div className="w-full h-full flex items-center justify-center bg-blue-100 italic text-[10px] text-black font-bold">
//                           {a.user_name?.charAt(0) || "?"}
//                         </div>
//                       )}
//                     </div>
//                     <span className="text-[11px] font-semibold text-black">{a.user_name || "System"}</span>
//                   </div>
 
//                   {config.details && (
//                     <div className="text-[12px] font-semibold text-black leading-relaxed shadow-lg p-2.5 rounded-lg bg-gray-50 border border-gray-100 group-hover:bg-white group-hover:shadow-sm transition-all">
//                       {config.details}
//                     </div>
//                   )}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
 
//       {activity.length > 5 && (
//         <button
//           onClick={() => setShowAll(!showAll)}
//           className="w-full mt-2 py-2 flex items-center justify-center gap-2 text-[12px] font-bold text-black hover:bg-blue-50 rounded-lg transition-colors border border-blue-100/50"
//         >
//           {showAll ? "Show Less" : `View ${activity.length - 5} More Events`}
//         </button>
//       )}
 
//       {!showAll && activity.length > 5 && (
//         <div className="absolute bottom-10 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
//       )}
//     </div>
//   );
// }
 
 


// import React, { useEffect, useState } from "react";
// import {
//   Play,
//   CheckCircle2,
//   CalendarRange,
//   RefreshCw,
//   AlertTriangle,
//   MessageSquare,
//   History,
//   Clock,
//   ChevronDown
// } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { getTaskActivityApi } from "../../app/api";
 
// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
// /* ================= HELPERS ================= */
 
// const safeParse = (val) => {
//   try {
//     if (!val) return {};
//     if (typeof val === "object") return val;
//     return JSON.parse(val);
//   } catch {
//     return val || {};
//   }
// };
 
// const formatSimpleDate = (date) => {
//   if (!date) return "";
//   const d = new Date(String(date).replace(' ', 'T'));
//   return d.toLocaleDateString("en-IN", {
//     day: 'numeric',
//     month: 'short'
//   });
// };
 
// const getActivityConfig = (a) => {
//   const oldData = safeParse(a.old_value);
//   const newData = safeParse(a.new_value);
 
//   // 🔥 Reason Fix: Reason could be in old_value (legacy) or new_value (new)
//   const reason = newData.reason || oldData.reason;
//   const newDueDate = newData.new_due_date || oldData.new_due_date;
//   const oldDueDate = oldData.old_due_date || oldData.due_date;
 
//   switch (a.action) {
//     case "TASK_STARTED":
//       return {
//         label: "Task Started",
//         icon: Play,
//         color: "bg-green-500",
//         shadow: "shadow-green-200",
//         details: null
//       };
//     case "TASK_COMPLETED":
//       return {
//         label: "Task Completed",
//         icon: CheckCircle2,
//         color: "bg-emerald-600",
//         shadow: "shadow-emerald-200",
//         details: "Goal reached! Task is now marked as DONE."
//       };


//       case "TASK_COMPLETED_LATE":
//         return {
//           label: "Completed (Late)",
//           icon: CheckCircle2,
//           color: "bg-amber-700",
//           shadow: "shadow-amber-300",
//           details: "Task finished past its deadline. Better late than never!"
//         };
//       case "TASK_OVERDUE":
//         return {
//           label: "Task Overdue",
//           icon: AlertTriangle,
//           color: "bg-red-600",
//           shadow: "shadow-red-300",
//           details: `This task missed its deadline (${oldDueDate ? String(oldDueDate).split(/[\sT]/)[0] : '—'}). Urgency required!`
//         };
   



//     case "DUE_DATE_UPDATED":
//       return {
//         label: "Timeline Adjusted",
//         icon: CalendarRange,
//         color: "bg-amber-500",
//         shadow: "shadow-amber-200",
//         details: (
//           <div className="space-y-1">
//             <div className="flex items-center gap-2 text-xs">
//               <span className="text-gray-400 line-through">
//                 {/* {oldDueDate ? oldDueDate.split(/[\sT]/)[0] : 'No Date'} */}
//                 {oldDueDate ? String(oldDueDate).split(/[\sT]/)[0] : 'No Date'}
 
//               </span>
//               <span className="text-amber-600 font-semibold">
//                 {/* → {newDueDate ? newDueDate.split(/[\sT]/)[0] : '—'} */}
//                 → {newDueDate ? String(newDueDate).split(/[\sT]/)[0] : '—'}
 
//               </span>
//             </div>
//             {reason && (
//               <div className="bg-amber-50 border-l-2 border-amber-400 p-2 text-[11px] italic text-amber-800 rounded">
//                 "{reason}"
//               </div>
//             )}
//           </div>
//         )
//       };
//     case "UPDATE":
//       // if (oldData.status && newData.status && oldData.status !== newData.status) {
//       //   return {
//       //     label: `Moved to ${newData.status}`,
//       //     icon: RefreshCw,
//       //     color: "bg-blue-500",
//       //     shadow: "shadow-blue-200",
//       //     details: `Status shifted from ${oldData.status} to ${newData.status}`
//       //   };
//       // }




//       const updatedFields = [];
//       if (newData.due_date) {
//         const d = new Date(newData.due_date);
//         const dateString = isNaN(d) ? newData.due_date : d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
//         updatedFields.push(`Due date → ${dateString}`);
//       }
//       if (newData.status) {
//         updatedFields.push(`Status → ${newData.status}`);
//       }
//       if (newData.priority) {
//         updatedFields.push(`Priority → ${newData.priority}`);
//       }
//       return {
//         label: "Task Updated",
//         icon: RefreshCw,
//         color: "bg-gray-400",
//         shadow: "shadow-gray-100",
//         // details: null
//         details: updatedFields.length > 0 ? (
//           <div className="space-y-1">
//             {updatedFields.map((f, idx) => (
//               <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
//                 <div className="w-1 h-1 rounded-full bg-gray-400" />
//                 {f}
//               </div>
//             ))}
//           </div>
//         ) : null
//       };
//     case "COMMENT":
//       return {
//         label: "Shared a thought",
//         icon: MessageSquare,
//         color: "bg-indigo-500",
//         shadow: "shadow-indigo-200",
//         details: "New comment added to the discussion."
//       };
//     default:
//       return {
//         label: a.action,
//         icon: History,
//         color: "bg-gray-400",
//         shadow: "shadow-gray-100",
//         details: null
//       };
//   }
// };
 
// /* ================= COMPONENT ================= */
 
// export default function TaskActivity({ taskId, activities }) {
//   const [activity, setActivity] = useState(activities || []);
//   const [loading, setLoading] = useState(!activities);
//   const [showAll, setShowAll] = useState(false);
 
//   useEffect(() => {
//     if (activities) {
//       setActivity(activities);
//       setLoading(false);
//       return;
//     }
 
//     if (!taskId) return;
//     setLoading(true);
 
//     getTaskActivityApi(taskId)
//       .then((res) => {
//         const rows = Array.isArray(res) ? res : res?.data || [];
//         setActivity(rows);
//       })
//       .catch(() => setActivity([]))
//       .finally(() => setLoading(false));
//   }, [taskId, activities]);
 
//   if (loading) {
//     return (
//       <div className="flex flex-col items-center justify-center p-8 space-y-3">
//         <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
//         <span className="text-xs text-gray-400 font-medium tracking-wide translate-x-1">Tracking journey...</span>
//       </div>
//     );
//   }
 
//   if (!activity.length) {
//     return (
//       <div className="flex flex-col items-center justify-center p-10 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-100">
//         <History className="w-8 h-8 text-gray-200 mb-2" />
//         <span className="text-xs text-gray-400 font-medium">No updates recorded yet</span>
//       </div>
//     );
//   }
 
//   const displayedActivity = showAll ? activity : activity.slice(0, 5);
 
//   return (
//     <div className="relative">
//       <div className="relative space-y-0.5">
//         <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 via-gray-100 to-transparent" />
 
//         <AnimatePresence mode="popLayout">
//           {displayedActivity.map((a, i) => {
//             const config = getActivityConfig(a);
//             const dateStr = formatSimpleDate(a.created_at);
//             const Icon = config.icon;
 
//             return (
//               <motion.div
//                 key={a.id || i}
//                 initial={{ opacity: 0, x: -10 }}
//                 animate={{ opacity: 1, x: 0 }}
//                 transition={{ delay: i * 0.05 }}
//                 className="group relative flex gap-6 pb-8 last:pb-2 pl-2"
//               >
//                 <div className="relative z-10 pt-1">
//                   <div className={`w-5 h-5 rounded-full ${config.color} ${config.shadow} shadow-lg flex items-center justify-center ring-4 ring-white group-hover:scale-110 transition-transform`}>
//                     <Icon className="w-2.5 h-2.5 text-white" />
//                   </div>
//                 </div>
 
//                 <div className="flex-1 -mt-0.5">
//                   <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
//                     <span className="text-[15px]  font-bold text-black group-hover:text-blue-600 group-hover:bg-white p-1 rounded-lg  transition-colors">
//                       {config.label}
//                     </span>
//                     <div className="flex items-center gap-2 text-[10px] shadow-lg text-black font-bold bg-gray-100 px-2 py-0.5 rounded-full w-fit">
//                       <Clock className="w-3 h-3" />
//                       {dateStr}
//                     </div>
//                   </div>
 
//                   <div className="flex items-center gap-2 mb-2">
//                     <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-100 border border-white shadow-sm flex-shrink-0">
//                       {a.user_profile_pic ? (
//                         <img
//                           src={`${IMAGE_BASE_URL}${a.user_profile_pic}`}
//                           alt={a.user_name}
//                           className="w-full h-full object-cover"
//                         />
//                       ) : (
//                         <div className="w-full h-full flex items-center justify-center bg-blue-100 italic text-[10px] text-black font-bold">
//                           {a.user_name?.charAt(0)}
//                         </div>
//                       )}
//                     </div>
//                     <span className="text-[11px] font-semibold text-black">{a.user_name}</span>
//                   </div>
 
//                   {config.details && (
//                     <div className="text-[12px] font-semibold text-black leading-relaxed  shadow-lg p-2.5 rounded-lg bg-gray-200 border border-gray-100 group-hover:bg-white group-hover:shadow-sm transition-all">
//                       {config.details}
//                     </div>
//                   )}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
 
//       {activity.length > 5 && (
//         <button
//           onClick={() => setShowAll(!showAll)}
//           className="w-full mt-2 py-2 flex items-center justify-center gap-2 text-[12px] font-bold text-white hover:bg-blue-50 hover:text-black rounded-lg transition-colors border border-blue-100/50"
//         >
//           {showAll ? "Show Less" : `View ${activity.length - 5} More Events`}
//           <ChevronDown className={`w-3 h-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
//         </button>
//       )}
 
//       {!showAll && activity.length > 5 && (
//         <div className="absolute bottom-10 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
//       )}
//     </div>
//   );
// }
 

